<template>
    <button
        :aria-label="label"
        :disabled="isLocked"
        class="rounded-xl p-4 flex items-center gap-3 sm:gap-4 bg-white transition-colors border border-gray-100 shadow-md"
        :class="{
            'shadow-none': state === 'complete',
            'hover:border-gray-300': (state === 'in_progress' || state === 'not_started') && !isLocked,
            'opacity-50 cursor-not-allowed': isLocked,
        }"
        @click="handleActionClick">
        <!-- Icon -->
        <div
            class="shrink-0 rounded-full h-5 w-5 flex items-center justify-center"
            :class="{
                'text-black opacity-70': state === 'in_progress' && !isLocked,
                'bg-[#EC6D62] text-white': state === 'not_started' && overdue && !isLocked,
                'bg-[#9596FF] text-white': state === 'complete',
                'bg-gray-300 text-gray-600': isLocked,
            }">
            <Checkmark v-if="state === 'complete'" width="10" height="10" />
            <LockSmall v-else-if="isLocked" width="12" height="12" />
            <i v-else-if="icon === 'user'" class="bi bi-person-fill" aria-hidden="true"></i>
            <i v-else-if="icon === 'users'" class="bi bi-people-fill" aria-hidden="true"></i>
            <i v-else-if="icon === 'overdue'" class="bi bi-exclamation-lg" aria-hidden="true"></i>
        </div>

        <!-- Content: title + dateRange stacked on mobile, inline on desktop -->
        <div class="flex-1 min-w-0">
            <div class="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                <div class="flex items-center gap-2 min-w-0">
                    <h3 class="body-small-semibold text-gray-900" :class="{ 'text-[#9596FF]': state === 'complete' }">
                        {{ state === "complete" ? "Complete" : label }}
                    </h3>
                    <div v-if="state === 'complete'" class="body-small-semibold text-gray-400">{{ label }}</div>
                </div>
                <span v-if="dateRange && state !== 'complete'" class="details text-gray-500 sm:whitespace-nowrap">{{ dateRange }}</span>
            </div>
        </div>

        <!-- Right side - status: stacked on mobile, inline on desktop -->
        <div class="flex items-center gap-2 sm:gap-3 shrink-0 opacity-70">
            <div class="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-4 sm:text-right">
                <div v-if="statusBadge">
                    <span
                        class="body-small inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs sm:text-sm whitespace-nowrap"
                        :class="{
                            'bg-[#DFEDC4]': statusBadge === STATUS_BADGE.IN_PROGRESS,
                            'bg-gray-100': statusBadge === STATUS_BADGE.NOT_STARTED,
                            'bg-valence-primary-200': statusBadge === STATUS_BADGE.VIEW_CHAT,
                        }">
                        {{ statusBadge }}
                    </span>
                </div>
            </div>

            <ArrowRight width="20" height="20" class="shrink-0 text-gray-400" />
        </div>
    </button>
</template>

<script setup>
import { useFetch } from "~vue/api";
import { formatCtaDateRange } from "~vue/composables/useCtaDateRange";
import ArrowRight from "~vue/icons/ArrowRight.vue";
import Checkmark from "~vue/icons/Checkmark.vue";
import LockSmall from "~vue/icons/LockSmall.vue";
import { openUrl } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { computed, ref } from "vue";

const props = defineProps({
    id: { type: Number, required: true },
    label: { type: String, required: true },
    startDate: { type: String, default: "" },
    dueDate: { type: String, default: "" },
    promptCallCode: { type: String, default: "" },
    existingConvoUrl: { type: String, default: null },
    managerEnabled: { type: Boolean, required: true },
    icEnabled: { type: Boolean, required: true },
    isCompleted: { type: Boolean, required: true },
    isLocked: { type: Boolean, default: false },
    track: { type: Object, default: () => ({}) },
});

const STATUS_BADGE = {
    IN_PROGRESS: "In progress",
    NOT_STARTED: "Not started",
    VIEW_CHAT: "View chat",
};

const now = DateTime.now();
const dueDate = DateTime.fromFormat(props.dueDate, "yyyy-MM-dd");
const daysUntilDue = Math.ceil(dueDate.diff(now, "days").days);

const dateRange = computed(() => {
    if (props.isCompleted) {
        return "";
    }
    return formatCtaDateRange(props.startDate || null, props.dueDate || null);
});

const state = computed(() => {
    if (props.isCompleted) {
        return "complete";
    }
    if (props.existingConvoUrl) {
        return "in_progress";
    }

    return "not_started";
});

const overdue = computed(() => {
    if (props.isCompleted || !dueDate.isValid) {
        return false;
    }

    return daysUntilDue < 0;
});

const statusBadge = computed(() => {
    switch (state.value) {
        case "in_progress":
            return STATUS_BADGE.IN_PROGRESS;
        case "not_started":
            return STATUS_BADGE.NOT_STARTED;
        case "complete":
            return STATUS_BADGE.VIEW_CHAT;
        default:
            return "";
    }
});

const icon = computed(() => {
    if (overdue.value && state.value === "not_started") {
        return "overdue";
    }
    if (props.managerEnabled) {
        return "users";
    }
    return "user";
});

const conversationPayload = ref(null);
const { data: conversationData, execute: startConversation } = useFetch("/api/coach/performance_reviews/conversations/", { immediate: false }).post(conversationPayload).json();

const handleActionClick = async () => {
    // Prevent action on locked CTAs
    if (props.isLocked) {
        return;
    }

    logUserInteraction(props.isCompleted ? "performance_review_card_cta_completed_clicked" : "performance_review_card_cta_clicked", { ...props, ...props.track });

    if (props.existingConvoUrl) {
        openUrl(props.existingConvoUrl);
        return;
    }

    conversationPayload.value = {
        relationship_id: null, // No per-person CTAs in Gen1
        performance_moment_cta_id: props.id,
    };
    await startConversation(true);
    if (conversationData.value?.coaching_session_url) {
        openUrl(conversationData.value.coaching_session_url);
    }
};
</script>
